import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { InferenceAPI, useTokenAndOrgId } from './utils'
import { toast } from 'react-hot-toast'
import { CreateAPIKeyArgs } from './inference.gen'
import dayjs from 'dayjs'

export const useGetApiKeys = (orgId: number) => {
  const { token } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['api-keys', token, orgId],
    enabled: !!token && !!orgId,
    queryFn: async () => {
      const result = await InferenceAPI.listAPIKeys()

      return {
        keys: result.api_keys.map((key) => ({
          ...key,
          id: key.id,
          formatted_created_at: dayjs(key.created_at).format('DD MMM YYYY'),
          formatted_last_used: dayjs(key.last_used_at).format('DD MMM YYYY')
            ? dayjs(key.last_used_at).format('DD MMM YYYY')
            : 'Never',
        })),
        total: result.total as number,
      }
    },
  })
}

export const useCreateApiKey = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateAPIKeyArgs) => {
      const result = await InferenceAPI.createAPIKey(payload)

      // Invalidate to refetch the list
      queryClient.invalidateQueries({
        queryKey: ['api-keys', token, orgId],
      })

      toast.success('Successfully created API key')
      return result.api_key
    },
  })
}

export const useDeleteApiKey = () => {
  const { token } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, orgId }: { id: number; orgId: number }) => {
      await InferenceAPI.deleteAPIKey({ api_key_id: id })

      // Invalidate to refetch the list
      queryClient.invalidateQueries({ queryKey: ['api-keys', token, orgId] })

      toast.success('Successfully deleted API key')
      return true
    },
  })
}
